// src/Components/FloorSelector.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import FloorAddModal from './FloorAddModal';

function getRoleFromToken() {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role || null;
  } catch {
    return null;
  }
}

// 이름만 보고 정렬 순서 계산 (level은 사용 안 함)
function floorOrder(name = '') {
  const s = String(name).trim().toUpperCase();
  const mB = s.match(/^B\s*(\d+)/);             // B2, B 2
  if (mB) return -parseInt(mB[1], 10);
  const mN = s.match(/^(\d+)\s*(F|층)?$/);       // 1, 1F, 1 층
  if (mN) return parseInt(mN[1], 10);
  if (/ROOF|옥상/.test(s)) return 999;
  return 0;                                      // 그 외는 0 근처
}

export default function FloorSelector() {
  const { locationName } = useParams(); // /locations/:locationName/floors
  const [floors, setFloors] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [checkedNames, setCheckedNames] = useState(new Set()); // ✅ 선택된 층 이름들
  const navigate = useNavigate();

  useEffect(() => {
    setIsAdmin(getRoleFromToken() === 'ADMIN');
    fetchFloors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName]);

  const fetchFloors = async () => {
    try {
      const res = await api.get('/floors', { params: { location: locationName } });
      const list = Array.isArray(res.data) ? res.data : [];
      setFloors(list);
      setErrorMsg('');

      // ✅ 목록 갱신 시, 존재하지 않는 name은 선택 해제
      setCheckedNames(prev => {
        const exist = new Set(list.map(f => f.name));
        const next = new Set();
        prev.forEach(nm => { if (exist.has(nm)) next.add(nm); });
        return next;
      });
    } catch (err) {
      console.error('층 목록 로드 실패', err?.response || err);
      setErrorMsg('층 목록을 불러오지 못했습니다.');
    }
  };

  const handleClickFloor = (floor) => {
    navigate(`/locations/${encodeURIComponent(locationName)}/floors/${encodeURIComponent(floor.name)}`);
  };

  const handleModalDone = () => {
    setShowModal(false);
    fetchFloors(); // 추가 후 갱신
  };

  // ✅ 체크박스 토글 (층 이름 기준)
  const toggleChecked = (name) => {
    setCheckedNames(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  // ✅ 선택된 층들 bulk 삭제
  const handleBulkDelete = async () => {
    if (checkedNames.size === 0) {
      alert('삭제할 층을 선택하세요.');
      return;
    }

    const ok = window.confirm(
      `총 ${checkedNames.size}개 층을 삭제하시겠습니까?\n(해당 층에 속한 디바이스도 함께 삭제될 수 있습니다.)`
    );
    if (!ok) return;

    setErrorMsg('');

    try {
      // LocationSelector와 동일하게 이름 기준으로 여러 번 요청
      for (const name of checkedNames) {
        await api.delete('/floors', {
          params: { location: locationName, name },
        });
      }
      await fetchFloors();
      setCheckedNames(new Set());
    } catch (err) {
      console.error('층 선택 삭제 실패', err?.response || err);
      setErrorMsg('일부 층 삭제에 실패했습니다. 새로고침 후 다시 시도하세요.');
    }
  };

  // 화면엔 name만 보여주되, 정렬은 자연스럽게
  const rows = useMemo(
    () => [...floors].sort((a, b) => floorOrder(a?.name) - floorOrder(b?.name)),
    [floors]
  );

  return (
    <div className="location-dashboard-container">
      <h1>{locationName} — 층 목록</h1>

      {isAdmin && (
        <div
          className="location-input-group"
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <button type="button" onClick={() => setShowModal(true)}>
            층 추가
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={checkedNames.size === 0}
            title={checkedNames.size === 0 ? '선택된 층이 없습니다' : ''}
          >
            선택 삭제
          </button>
        </div>
      )}

      {showModal && (
        <FloorAddModal
          locationName={locationName}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalDone}
        />
      )}

      {errorMsg && (
        <div role="alert" style={{ color: 'crimson', margin: '8px 0' }}>
          {errorMsg}
        </div>
      )}

      <table className="location-table">
        <thead>
          <tr>
            <th>층 이름</th>
            {isAdmin && <th style={{ width: 80, textAlign: 'center' }}>선택</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id ?? f.name}>
              {/* 이름 칸 클릭 시 층 상세로 이동 */}
              <td
                onClick={() => handleClickFloor(f)}
                style={{ cursor: 'pointer' }}
                title={f.name}
              >
                {f.name}
              </td>

              {/* Admin일 때만 체크박스 표시 */}
              {isAdmin && (
  <td style={{ textAlign: 'center' }}>
    <input
      type="checkbox"
      checked={checkedNames.has(f.name)}
      onChange={() => toggleChecked(f.name)}
      style={{
        transform: 'scale(1.2)',
        accentColor: '#00eaff',   // 네온 블루
        cursor: 'pointer',
      }}
    />
  </td>
)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 2 : 1}>등록된 층이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
